import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getSessions, revokeSession, type Session } from '../api/client';
import { ArrowLeft, Monitor, Smartphone, Tablet, Loader2 } from 'lucide-react';

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function DeviceIcon({ type }: { type: string | null }) {
  const iconProps = { size: 16, color: colors.gray[600] };
  switch (type) {
    case 'Mobile':
      return <Smartphone {...iconProps} />;
    case 'Tablet':
      return <Tablet {...iconProps} />;
    default:
      return <Monitor {...iconProps} />;
  }
}

export function SessionsPage() {
  useDocumentTitle('Active Sessions');
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      const data = await getSessions();
      setSessions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (sessionId: string) => {
    try {
      setRevokingId(sessionId);
      await revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke session');
    } finally {
      setRevokingId(null);
    }
  };

  const formatLocation = (session: Session) => {
    const parts: string[] = [];
    if (session.locationCity) parts.push(session.locationCity);
    if (session.locationCountry) parts.push(session.locationCountry);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button onClick={() => navigate('/security')} style={styles.backButton}>
            <ArrowLeft size={20} />
            <span>Back to Security</span>
          </button>

          <h1 style={styles.title}>Active Sessions</h1>

          {error && <p style={styles.error}>{error}</p>}

          {isLoading ? (
            <div style={styles.loadingContainer}>
              <Loader2 size={24} color={colors.gray[400]} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : sessions.length === 0 ? (
            <div style={styles.emptyCard}>
              <p style={styles.emptyText}>No active sessions found.</p>
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Device</th>
                    <th style={styles.th}>Location</th>
                    <th style={styles.th}>IP Address</th>
                    <th style={styles.th}>Last Active</th>
                    <th style={{ ...styles.th, width: '80px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={styles.deviceCell}>
                          <DeviceIcon type={session.deviceType} />
                          <span style={styles.deviceName}>
                            {session.deviceName || 'Unknown Device'}
                          </span>
                          {session.current && (
                            <span style={styles.currentBadge}>Current</span>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>{formatLocation(session)}</td>
                      <td style={{ ...styles.td, color: colors.gray[400] }}>
                        {session.ipAddress || '—'}
                      </td>
                      <td style={styles.td}>
                        {session.lastActiveAt
                          ? formatRelativeTime(session.lastActiveAt)
                          : session.createdAt
                            ? formatRelativeTime(session.createdAt)
                            : '—'}
                      </td>
                      <td style={styles.td}>
                        {!session.current && (
                          <button
                            style={styles.revokeButton}
                            onClick={() => handleRevoke(session.id)}
                            disabled={revokingId === session.id}
                          >
                            {revokingId === session.id ? (
                              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                            ) : (
                              'Revoke'
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '24px',
  },
  error: {
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#FEE2E2',
    borderRadius: '8px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
  emptyCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '32px',
    textAlign: 'center' as const,
  },
  emptyText: {
    color: colors.gray[600],
    fontSize: '14px',
  },
  tableWrapper: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    borderBottom: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.gray[100],
  },
  tr: {
    borderBottom: `1px solid ${colors.gray[300]}`,
  },
  td: {
    padding: '12px 16px',
    fontSize: '13px',
    color: colors.gray[600],
    verticalAlign: 'middle' as const,
  },
  deviceCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  deviceName: {
    fontSize: '13px',
    fontWeight: 500,
    color: colors.ink.black,
  },
  currentBadge: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#16A34A',
    backgroundColor: '#DCFCE7',
    padding: '2px 8px',
    borderRadius: '100px',
  },
  revokeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};
